import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { ToggleGroup, ToggleGroupItem } from '@radix-ui/react-toggle-group';
import { Key, useMemo, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { useMedia } from 'react-use';
import { Chain } from 'viem';

import { NETWORK_NAME_PER_CHAIN_ID, SUPPORTED_CHAINS } from '@/constants/chain';

import { useMeasure } from '@/hooks/useMeasure';

import { popover } from '@/styles/popover';
import { tabsStyles } from '@/styles/tabs';
import { breakpoints } from '@/styles/util';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { CheckmarkIcon } from '@/ui/Icons/CheckmarkIcon';
import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';
import {
  MatchaTooltipPopover,
  MatchaTooltip,
  MatchaTooltipTrigger,
  MatchaTooltipContent,
} from '@/ui/MatchaTooltip';

import { PreviewToken } from '../../TokenPreviewPopover';

// Measured from small gray tab chips (All ~39px, chain icons ~38px) + gap-2.
const CHIP_WIDTH = 39;
const GAP = 8;
const MORE_BUTTON_WIDTH = 81;

const NO_FILTER = 'all';
const ALL_NETWORKS = {
  title: 'All',
  filter: NO_FILTER,
} as const;

/** Max chips that fit in `availableWidth` with `GAP` between them. */
function maxChipsFitting(availableWidth: number): number {
  if (availableWidth < CHIP_WIDTH) return 0;
  return Math.floor((availableWidth + GAP) / (CHIP_WIDTH + GAP));
}

interface Props {
  chains?: Chain[];
  onChange: (chainId: number | undefined) => void;
  onDropdownOpenChange?: (isOpen: boolean) => void;
  defaultValue: number | undefined;
  setPreviewTokenWithDelay: (token: PreviewToken | null) => void;
}

const { container, tab } = tabsStyles({ variant: 'secondary' });

export const SearchChainFilters = ({
  chains,
  defaultValue,
  onChange,
  onDropdownOpenChange,
  setPreviewTokenWithDelay,
}: Props) => {
  const { base, item } = popover();
  const FILTERS = [
    ALL_NETWORKS,
    ...(chains || SUPPORTED_CHAINS).map(({ id }) => ({
      title: NETWORK_NAME_PER_CHAIN_ID[id],
      filter: id.toString(),
    })),
  ];

  const [value, setValue] = useState(defaultValue?.toString() || FILTERS[0].filter);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  function handleChangeFilter(filter: Key) {
    if (!filter) return;
    setValue(filter.toString());
    onChange(filter !== ALL_NETWORKS.filter ? Number(filter) : undefined);
  }

  const containerRef = useRef<HTMLDivElement>(null);

  const {
    nodeRef,
    bounds: { width: containerWidth },
  } = useMeasure<HTMLDivElement>();

  const mergedRef = useMemo(() => mergeRefs([containerRef, nodeRef]), [containerRef, nodeRef]);
  const isMobile = useMedia(breakpoints.mobile, false);

  const filtersLength = FILTERS.length;
  // contentRect width already excludes padding; pack chips to fill the row,
  // and only reserve More when the full list would overflow.
  const maxItemsWithoutMore = maxChipsFitting(containerWidth);
  const shouldShowMoreButton = filtersLength > maxItemsWithoutMore;
  const maxToggleItems = isMobile
    ? filtersLength
    : shouldShowMoreButton
      ? maxChipsFitting(containerWidth - MORE_BUTTON_WIDTH - GAP)
      : maxItemsWithoutMore;
  const toggleItems = containerWidth ? FILTERS.slice(0, maxToggleItems) : [];
  const dropdownItems = containerWidth ? FILTERS.slice(maxToggleItems) : [];
  const selectedDropdownItem =
    dropdownItems.length > 0 ? dropdownItems.find(({ filter }) => filter === value) : undefined;

  return (
    <div
      className="flex gap-x-2 shrink-0 p-4 overflow-x-auto tablet:overflow-x-[initial] w-full [&::-webkit-scrollbar]:hidden"
      ref={mergedRef}
      onMouseEnter={() => setPreviewTokenWithDelay(null)}
    >
      {toggleItems.length > 0 && (
        <ToggleGroup
          aria-label="Filter results by chain"
          className={container()}
          onValueChange={handleChangeFilter}
          type="single"
          value={value}
        >
          {toggleItems.map(({ title, filter }) => {
            if (filter === NO_FILTER)
              return (
                <ToggleGroupItem
                  className={tab({
                    class: ['whitespace-nowrap', 'px-[11px]'],
                    active: value === ALL_NETWORKS.filter,
                  })}
                  key="all"
                  value={ALL_NETWORKS.filter}
                >
                  {ALL_NETWORKS.title}
                </ToggleGroupItem>
              );

            return (
              <MatchaTooltip key={title}>
                <MatchaTooltipTrigger>
                  <div className="shrink-0">
                    <ToggleGroupItem
                      className={tab({
                        class: ['whitespace-nowrap', 'px-[11px]'],
                        active: value === filter,
                      })}
                      value={filter}
                    >
                      <ChainAvatar chainId={Number(filter)} alt={title} size={16} />
                    </ToggleGroupItem>
                  </div>
                </MatchaTooltipTrigger>
                <MatchaTooltipContent>{title}</MatchaTooltipContent>
                <MatchaTooltipPopover />
              </MatchaTooltip>
            );
          })}
        </ToggleGroup>
      )}

      {dropdownItems.length > 0 && (
        <DropdownMenu
          /* Keep modal false or the page may become unresponsive
           * When dismissing parent component while Dropdown is open,
           * the Dropdown Menu's overlay sticks and covers the page. */
          modal={false}
          onOpenChange={(isOpen: boolean) => {
            setIsDropdownOpen(isOpen);
            onDropdownOpenChange?.(isOpen);
          }}
          open={isDropdownOpen}
        >
          <MatchaTooltip>
            <MatchaTooltipTrigger>
              <div>
                <DropdownMenuTrigger
                  className={tab({
                    class: ['whitespace-nowrap', 'px-[11px]'],
                    active: !!selectedDropdownItem,
                  })}
                >
                  {selectedDropdownItem != null &&
                  selectedDropdownItem.filter !== ALL_NETWORKS.filter ? (
                    <ChainAvatar
                      chainId={Number(selectedDropdownItem.filter)}
                      alt={selectedDropdownItem.title}
                      size={16}
                      className="mr-2"
                    />
                  ) : (
                    <span className="mr-2">More</span>
                  )}
                  <ChevronDownIcon />
                </DropdownMenuTrigger>
              </div>
            </MatchaTooltipTrigger>
            <MatchaTooltipContent>
              {selectedDropdownItem?.title || `+${dropdownItems.length} Networks`}
            </MatchaTooltipContent>
            <MatchaTooltipPopover />
          </MatchaTooltip>
          <DropdownMenuPortal container={containerRef.current}>
            <DropdownMenuContent align="end" className={base()}>
              {dropdownItems.map(({ title, filter }) => {
                const selected = value === filter;
                return (
                  <DropdownMenuItem
                    className={item()}
                    key={title}
                    onSelect={() => handleChangeFilter(filter)}
                  >
                    <ChainAvatar chainId={Number(filter)} alt={title} size={16} className="mr-2" />
                    <span>{title}</span>
                    {selected && <CheckmarkIcon className="ml-auto" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenu>
      )}
    </div>
  );
};
